import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createId as makeId, createUuid } from "@/store/shared/id";
import { supabase } from "@/store/shared/supabase";
import { dispatchDataError, describeSupabaseError } from "@/store/shared/dataErrors";
import { fetchScopeIds, EMPTY_SCOPE_IDS } from "@/store/shared/scopeIds";
import { useSession } from "@/session/SessionProvider";
import {
  photosFromDb,
  prioriteFromDb,
  prioriteToDb,
  statutFromDb,
  statutToDb,
} from "@/store/shared/maintenanceMapping";
import { documentTypeFromDb, documentTypeToDb } from "@/store/shared/documentMapping";
import {
  buildDocumentPath,
  createSignedDocumentUrl,
  dataUrlToBlob,
  removeDocumentObject,
  uploadDocumentBlob,
} from "@/store/shared/storage";
import { isoDate, daysBetween, echeanceISO, periodRange, startOfDay, toMonthKey } from "@/pages/agence/loyers/periods";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import type { PlatformBien, PlatformContrat, PlatformImmeuble, PlatformLocataire } from "@/store/platform/types";
import type { PlatformDepense, PlatformLoyer, PlatformPaiement } from "@/store/platform/bloc6";
import type {
  AgenceBien,
  AgenceBienInput,
  AgenceContrat,
  AgenceContratInput,
  AgenceDepense,
  AgenceDepenseInput,
  AgenceDocument,
  AgenceDocumentInput,
  AgenceImmeuble,
  AgenceImmeubleInput,
  AgenceIntervention,
  AgenceInterventionInput,
  AgenceInterventionStatut,
  AgenceLocataire,
  AgenceLocataireInput,
  AgenceLoyer,
  AgencePaiement,
  AgencePaiementInput,
  AgenceStatutLoyer,
  ProprietaireSummary,
} from "./types";
import { AGENCE_IMPAYE_SEUIL_JOURS, isAgenceImpaye } from "./types";

interface AgenceLoyersStats {
  total: number;
  payes: number;
  aVenir: number;
  enRetard: number;
  impayes: number;
  montantDuMois: number;
  montantImpayes: number;
}

interface AgencePaiementsStats {
  total: number;
  montantTotal: number;
  ceMois: number;
}

interface AgenceDepensesStats {
  total: number;
  montantTotal: number;
  ceMois: number;
}

interface AgenceInterventionsStats {
  total: number;
  nouveaux: number;
  enCours: number;
  enAttente: number;
  termines: number;
  annules: number;
  coutTotal: number;
}

interface AgenceDataContextValue {
  immeubles: AgenceImmeuble[];
  biens: AgenceBien[];
  locataires: AgenceLocataire[];
  contrats: AgenceContrat[];
  paiements: AgencePaiement[];
  depenses: AgenceDepense[];
  interventions: AgenceIntervention[];
  documents: AgenceDocument[];
  loyers: AgenceLoyer[];
  impayes: AgenceLoyer[];
  periodes: string[];
  statsLoyers: AgenceLoyersStats;
  statsPaiements: AgencePaiementsStats;
  statsDepenses: AgenceDepensesStats;
  statsInterventions: AgenceInterventionsStats;
  addImmeuble: (input: AgenceImmeubleInput) => AgenceImmeuble;
  addBien: (input: AgenceBienInput) => AgenceBien;
  addLocataire: (input: AgenceLocataireInput) => AgenceLocataire;
  addContrat: (input: AgenceContratInput) => AgenceContrat;
  addPaiement: (input: AgencePaiementInput) => AgencePaiement;
  addDepense: (input: AgenceDepenseInput) => AgenceDepense;
  removeImmeuble: (id: string) => void;
  removeBien: (id: string) => void;
  removeLocataire: (id: string) => void;
  removeContrat: (id: string) => void;
  removePaiement: (id: string) => void;
  removeDepense: (id: string) => void;
  updateImmeuble: (id: string, input: AgenceImmeubleInput) => void;
  updateBien: (id: string, input: AgenceBienInput) => void;
  updateLocataire: (id: string, input: AgenceLocataireInput) => void;
  updateContrat: (id: string, input: AgenceContratInput) => void;
  updateDepense: (id: string, input: AgenceDepenseInput) => void;
  addIntervention: (input: AgenceInterventionInput) => AgenceIntervention;
  removeIntervention: (id: string) => void;
  updateIntervention: (id: string, input: AgenceInterventionInput) => void;
  updateInterventionStatut: (id: string, statut: AgenceInterventionStatut) => void;
  addDocument: (input: AgenceDocumentInput) => AgenceDocument;
  removeDocument: (id: string) => void;
  updateDocument: (id: string, input: AgenceDocumentInput) => void;
  getImmeuble: (id: string) => AgenceImmeuble | undefined;
  getBien: (id: string) => AgenceBien | undefined;
  getLocataire: (id: string) => AgenceLocataire | undefined;
  getContrat: (id: string) => AgenceContrat | undefined;
  getPaiement: (id: string) => AgencePaiement | undefined;
  getDepense: (id: string) => AgenceDepense | undefined;
  getIntervention: (id: string) => AgenceIntervention | undefined;
  getDocument: (id: string) => AgenceDocument | undefined;
  getLoyer: (id: string) => AgenceLoyer | undefined;
  biensByImmeuble: (immeubleId: string) => AgenceBien[];
  biensByProprietaire: (proprietaireId: string) => AgenceBien[];
  immeublesByProprietaire: (proprietaireId: string) => AgenceImmeuble[];
  contratsByImmeuble: (immeubleId: string) => AgenceContrat[];
  contratsByBien: (bienId: string) => AgenceContrat[];
  contratsByLocataire: (locataireId: string) => AgenceContrat[];
  loyersByContrat: (contratId: string) => AgenceLoyer[];
  loyersByLocataire: (locataireId: string) => AgenceLoyer[];
  loyersByBien: (bienId: string) => AgenceLoyer[];
  loyersByImmeuble: (immeubleId: string) => AgenceLoyer[];
  paiementsByContrat: (contratId: string) => AgencePaiement[];
  paiementsByContratPeriode: (contratId: string, periode: string) => AgencePaiement[];
  totalPaye: (contratId: string, periode: string) => number;
  totalPayeContrat: (contratId: string) => number;
  depensesByImmeuble: (immeubleId: string) => AgenceDepense[];
  depensesByBien: (bienId: string) => AgenceDepense[];
  depensesByProprietaire: (proprietaireId: string) => AgenceDepense[];
  interventionsByImmeuble: (immeubleId: string) => AgenceIntervention[];
  interventionsByBien: (bienId: string) => AgenceIntervention[];
  interventionsByProprietaire: (proprietaireId: string) => AgenceIntervention[];
  documentsByImmeuble: (immeubleId: string) => AgenceDocument[];
  documentsByBien: (bienId: string) => AgenceDocument[];
  documentsByProprietaire: (proprietaireId: string) => AgenceDocument[];
  getSummary: (proprietaireId: string) => ProprietaireSummary;
}

const AgenceDataContext = createContext<AgenceDataContextValue | null>(null);

const sumBy = <T,>(items: T[], getValue: (item: T) => number) =>
  items.reduce((total, item) => total + getValue(item), 0);

type Row = Record<string, unknown>;
const strValue = (value: unknown): string => (typeof value === "string" ? value : "");
const nullableValue = (value: string): string | null => (value && value.trim() ? value : null);
const toNumber = (value: unknown): number => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Ligne `maintenances` → modèle Agence (le propriétaire est déduit de l'immeuble). */
function rowToAgenceIntervention(row: Row, ownerByImmeuble: Map<string, string>): AgenceIntervention {
  const immeubleId = strValue(row.immeuble_id);
  return {
    id: strValue(row.id),
    proprietaireId: ownerByImmeuble.get(immeubleId) ?? "",
    immeubleId,
    bienId: strValue(row.bien_id),
    probleme: strValue(row.titre),
    description: strValue(row.description),
    priorite: prioriteFromDb(strValue(row.priorite)),
    statut: statutFromDb(strValue(row.statut)),
    photos: photosFromDb(row.photos),
    cout: toNumber(row.cout),
    prestataire: strValue(row.prestataire),
    date: strValue(row.date_signalement),
    createdAt: strValue(row.created_at),
  };
}

function agenceInterventionToPayload(input: AgenceInterventionInput): Record<string, unknown> {
  return {
    immeuble_id: nullableValue(input.immeubleId),
    bien_id: nullableValue(input.bienId),
    titre: input.probleme,
    description: nullableValue(input.description),
    priorite: prioriteToDb(input.priorite),
    statut: statutToDb(input.statut),
    date_signalement: input.date,
    prestataire: nullableValue(input.prestataire),
    cout: Math.max(0, Number(input.cout) || 0),
    photos: input.photos,
  };
}

function fileNameFromDocPath(path: string): string {
  const segment = path.split("/").pop() ?? path;
  return segment.replace(/^\d+-[a-z0-9]{6}-/, "");
}

/* ------------------------------------------------------------------ */
/* Passerelles vers le store unifié de la plateforme (immeubles/biens/  */
/* locataires partagés par les trois espaces, scope « agence »).        */
/* ------------------------------------------------------------------ */

function toAgenceImmeuble(source: PlatformImmeuble): AgenceImmeuble {
  return {
    id: source.id,
    proprietaireId: source.proprietaireId,
    nom: source.nom,
    description: source.description,
    photo: source.photo,
    regionId: source.regionId,
    provinceId: source.provinceId,
    communeId: source.communeId,
    secteurId: source.secteurId,
    region: source.region,
    province: source.province,
    commune: source.commune,
    secteur: source.secteur,
    adresse: source.adresse,
    niveaux: source.niveaux,
    infosComplementaires: source.infosComplementaires,
    createdAt: source.createdAt,
  };
}

function toAgenceBien(source: PlatformBien): AgenceBien {
  return {
    id: source.id,
    proprietaireId: source.proprietaireId,
    immeubleId: source.immeubleId,
    type: source.type as AgenceBien["type"],
    reference: source.reference,
    etage: source.etage,
    numero: source.numero,
    surface: source.surface,
    loyerMensuel: source.loyerMensuel,
    statut: source.statut as AgenceBien["statut"],
    description: source.description,
    infosComplementaires: source.infosComplementaires,
    createdAt: source.createdAt,
  };
}

function toAgenceLocataire(source: PlatformLocataire): AgenceLocataire {
  return {
    id: source.id,
    prenom: source.prenom,
    nom: source.nom,
    telephone: source.telephone,
    whatsapp: source.whatsapp,
    email: source.email,
    adresse: source.adresse,
    profession: source.profession,
    typePiece: source.typePiece as AgenceLocataire["typePiece"],
    numeroPiece: source.numeroPiece,
    contactUrgence: source.contactUrgence,
    notes: source.notes,
    proprietaireId: source.proprietaireId,
    agenceId: source.agenceId,
    createdAt: source.createdAt,
  };
}

function toAgenceContrat(source: PlatformContrat): AgenceContrat {
  return {
    id: source.id,
    proprietaireId: source.proprietaireId,
    immeubleId: source.immeubleId,
    bienId: source.bienId,
    locataireId: source.locataireId,
    dateDebut: source.dateDebut,
    dateFin: source.dateFin,
    loyerMensuel: source.loyerMensuel,
    caution: source.caution,
    avance: source.avance,
    jourEcheance: source.jourEcheance,
    conditionsParticulieres: source.conditionsParticulieres,
    document: source.document,
    statut: source.statut,
    createdAt: source.createdAt,
  };
}

function toAgencePaiement(source: PlatformPaiement): AgencePaiement {
  return {
    id: source.id,
    contratId: source.contratId,
    periode: source.periode,
    montant: source.montant,
    date: source.date,
    mode: source.mode,
    reference: source.reference,
    note: source.note,
    createdAt: source.createdAt,
  };
}

function toAgenceDepense(source: PlatformDepense): AgenceDepense {
  return {
    id: source.id,
    proprietaireId: source.proprietaireId,
    immeubleId: source.immeubleId,
    bienId: source.bienId,
    libelle: source.libelle,
    categorie: source.categorie,
    montant: source.montant,
    date: source.date,
    description: source.description,
    justificatif: source.justificatif,
    createdAt: source.createdAt,
  };
}

function toAgenceLoyer(source: PlatformLoyer): AgenceLoyer {
  const statut: AgenceStatutLoyer = source.statut === "annule" ? "a-venir" : source.statut;
  return {
    id: source.id,
    contratId: source.contratId,
    proprietaireId: source.proprietaireId,
    immeubleId: source.immeubleId,
    bienId: source.bienId,
    locataireId: source.locataireId,
    periode: source.periode,
    montant: source.montant,
    totalPaye: source.totalPaye,
    reste: source.reste,
    echeance: source.echeance,
    statut,
    joursRetard: source.joursRetard,
  };
}

/**
 * Données de l'espace Agence (frontend uniquement).
 * Aucune donnée fictive : les collections démarrent vides et ne contiennent
 * que les éléments réellement créés par l'agence.
 * Relations garanties : chaque immeuble est rattaché à un propriétaire, chaque
 * bien à un immeuble (donc à son propriétaire), chaque contrat relie un bien à
 * un locataire. Les loyers proviennent des contrats actifs, les paiements
 * mettent à jour les loyers, et les impayés correspondent aux loyers non réglés.
 * Le jour où la base de données est branchée, on remplace ces mutations par des
 * appels Supabase sans toucher aux pages.
 */
export function AgenceDataProvider({ children }: { children: ReactNode }) {
  const platform = usePlatformData();

  const immeubles = useMemo(
    () => platform.immeubles.filter((item) => item.scope === "agence").map(toAgenceImmeuble),
    [platform.immeubles]
  );
  const biens = useMemo(
    () => platform.biens.filter((item) => item.scope === "agence").map(toAgenceBien),
    [platform.biens]
  );
  const locataires = useMemo(
    () => platform.locataires.filter((item) => item.scope === "agence").map(toAgenceLocataire),
    [platform.locataires]
  );
  const contrats = useMemo(
    () => platform.contrats.filter((item) => item.scope === "agence").map(toAgenceContrat),
    [platform.contrats]
  );
  const { session } = useSession();
  const paiements = useMemo(
    () => platform.paiements.filter((item) => item.scope === "agence").map(toAgencePaiement),
    [platform.paiements]
  );
  const depenses = useMemo(
    () => platform.depenses.filter((item) => item.scope === "agence").map(toAgenceDepense),
    [platform.depenses]
  );
  const [interventions, setInterventions] = useState<AgenceIntervention[]>([]);
  const [documents, setDocuments] = useState<AgenceDocument[]>([]);

  const scopeRef = useRef(EMPTY_SCOPE_IDS);
  const documentPathRef = useRef<Map<string, string>>(new Map());
  const ownerByImmeubleRef = useRef<Map<string, string>>(new Map());

  // Table immeuble → propriétaire (pour rattacher interventions/documents au bon propriétaire).
  useEffect(() => {
    const map = new Map<string, string>();
    platform.immeubles.forEach((immeuble) => map.set(immeuble.id, immeuble.proprietaireId));
    ownerByImmeubleRef.current = map;
  }, [platform.immeubles]);

  const loadInterventions = useCallback(async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from("maintenances")
        .select("*")
        .order("date_signalement", { ascending: false });
      if (fetchError) throw fetchError;
      setInterventions(
        ((data as Row[] | null) ?? []).map((row) => rowToAgenceIntervention(row, ownerByImmeubleRef.current))
      );
    } catch (caught) {
      console.error("[Agence:maintenance] chargement échoué", caught);
      dispatchDataError({
        scope: "Maintenance",
        message: "Impossible de charger les interventions.",
        technical: describeSupabaseError(caught),
      });
    }
  }, []);

  const loadDocuments = useCallback(async () => {
    try {
      scopeRef.current = await fetchScopeIds();
      const { data, error: fetchError } = await supabase
        .from("documents")
        .select("*")
        .order("created_at", { ascending: false });
      if (fetchError) throw fetchError;
      const rows = (data as Row[] | null) ?? [];
      const paths = new Map<string, string>();
      const ownerMap = ownerByImmeubleRef.current;
      const items = await Promise.all(
        rows.map(async (row) => {
          const id = strValue(row.id);
          const path = strValue(row.url);
          paths.set(id, path);
          const signed = await createSignedDocumentUrl(path);
          const immeubleId = strValue(row.immeuble_id);
          return {
            id,
            nom: strValue(row.nom),
            type: documentTypeFromDb(strValue(row.type_document)) as AgenceDocument["type"],
            date: strValue(row.date_document),
            proprietaireId: ownerMap.get(immeubleId) ?? "",
            immeubleId,
            bienId: strValue(row.bien_id),
            contratId: strValue(row.contrat_id),
            locataireId: strValue(row.locataire_id),
            fichierUrl: signed || path,
            fichierNom: fileNameFromDocPath(path),
            fichierType: strValue(row.type_fichier),
            fichierTaille: toNumber(row.taille),
            createdAt: strValue(row.created_at),
          } satisfies AgenceDocument;
        })
      );
      documentPathRef.current = paths;
      setDocuments(items);
    } catch (caught) {
      console.error("[Agence:documents] chargement échoué", caught);
      dispatchDataError({
        scope: "Documents",
        message: "Impossible de charger les documents.",
        technical: describeSupabaseError(caught),
      });
    }
  }, []);

  useEffect(() => {
    if (!session) {
      setInterventions([]);
      setDocuments([]);
      return;
    }
    void loadInterventions();
    void loadDocuments();
  }, [session, loadInterventions, loadDocuments]);

  const addImmeuble = useCallback(
    (input: AgenceImmeubleInput) => toAgenceImmeuble(platform.addImmeuble({ ...input, scope: "agence" })),
    [platform.addImmeuble]
  );

  const addBien = useCallback(
    (input: AgenceBienInput) => toAgenceBien(platform.addBien({ ...input, scope: "agence" })),
    [platform.addBien]
  );

  const addLocataire = useCallback(
    (input: AgenceLocataireInput) => toAgenceLocataire(platform.addLocataire({ ...input, scope: "agence" })),
    [platform.addLocataire]
  );

  const addContrat = useCallback(
    (input: AgenceContratInput) => toAgenceContrat(platform.addContrat({ ...input, scope: "agence" })),
    [platform.addContrat]
  );

  const addPaiement = useCallback(
    (input: AgencePaiementInput) => toAgencePaiement(platform.addPaiement(input)),
    [platform.addPaiement]
  );

  const addDepense = useCallback(
    (input: AgenceDepenseInput) => toAgenceDepense(platform.addDepense(input)),
    [platform.addDepense]
  );

  const removeImmeuble = useCallback(
    (id: string) => {
      platform.removeImmeuble(id);
    },
    [platform.removeImmeuble]
  );

  const removeBien = useCallback(
    (id: string) => {
      platform.removeBien(id);
    },
    [platform.removeBien]
  );

  const removeLocataire = useCallback(
    (id: string) => {
      platform.removeLocataire(id);
    },
    [platform.removeLocataire]
  );

  const removeContrat = useCallback(
    (id: string) => {
      platform.removeContrat(id);
    },
    [platform.removeContrat]
  );

  const removePaiement = useCallback(
    (id: string) => {
      platform.removePaiement(id);
    },
    [platform.removePaiement]
  );

  const removeDepense = useCallback(
    (id: string) => {
      platform.removeDepense(id);
    },
    [platform.removeDepense]
  );

  const updateImmeuble = useCallback(
    (id: string, input: AgenceImmeubleInput) => {
      platform.updateImmeuble(id, { ...input, scope: "agence" });
    },
    [platform.updateImmeuble]
  );

  const updateBien = useCallback(
    (id: string, input: AgenceBienInput) => {
      platform.updateBien(id, { ...input, scope: "agence" });
    },
    [platform.updateBien]
  );

  const updateLocataire = useCallback(
    (id: string, input: AgenceLocataireInput) => {
      platform.updateLocataire(id, { ...input, scope: "agence" });
    },
    [platform.updateLocataire]
  );

  const updateContrat = useCallback(
    (id: string, input: AgenceContratInput) => {
      platform.updateContrat(id, { ...input, scope: "agence" });
    },
    [platform.updateContrat]
  );

  const updateDepense = useCallback(
    (id: string, input: AgenceDepenseInput) => {
      platform.updateDepense(id, input);
    },
    [platform.updateDepense]
  );

  const addIntervention = useCallback(
    (input: AgenceInterventionInput) => {
      const optimistic: AgenceIntervention = { ...input, id: createUuid(), createdAt: new Date().toISOString() };
      setInterventions((prev) => [optimistic, ...prev]);
      if (!input.immeubleId && !input.bienId) {
        setInterventions((prev) => prev.filter((item) => item.id !== optimistic.id));
        dispatchDataError({
          scope: "Maintenance",
          message: "Sélectionnez un immeuble ou un bien pour l'intervention.",
        });
        return optimistic;
      }
      void (async () => {
        const { error: insertError } = await supabase.from("maintenances").insert(agenceInterventionToPayload(input));
        if (insertError) {
          setInterventions((prev) => prev.filter((item) => item.id !== optimistic.id));
          dispatchDataError({
            scope: "Maintenance",
            message: "Impossible d'enregistrer l'intervention.",
            technical: describeSupabaseError(insertError),
          });
          return;
        }
        void loadInterventions();
      })();
      return optimistic;
    },
    [loadInterventions]
  );

  const removeIntervention = useCallback(
    (id: string) => {
      const previous = interventions.find((item) => item.id === id);
      setInterventions((prev) => prev.filter((item) => item.id !== id));
      void (async () => {
        const { error: deleteError } = await supabase.from("maintenances").delete().eq("id", id);
        if (deleteError) {
          if (previous) setInterventions((prev) => [previous, ...prev]);
          dispatchDataError({
            scope: "Maintenance",
            message: "Impossible de supprimer l'intervention.",
            technical: describeSupabaseError(deleteError),
          });
          return;
        }
        void loadInterventions();
      })();
    },
    [interventions, loadInterventions]
  );

  const updateIntervention = useCallback(
    (id: string, input: AgenceInterventionInput) => {
      const previous = interventions.find((item) => item.id === id);
      setInterventions((prev) => prev.map((item) => (item.id === id ? { ...item, ...input } : item)));
      void (async () => {
        const { error: updateError } = await supabase
          .from("maintenances")
          .update(agenceInterventionToPayload(input))
          .eq("id", id);
        if (updateError) {
          if (previous) setInterventions((prev) => prev.map((item) => (item.id === id ? previous : item)));
          dispatchDataError({
            scope: "Maintenance",
            message: "Impossible de modifier l'intervention.",
            technical: describeSupabaseError(updateError),
          });
          return;
        }
        void loadInterventions();
      })();
    },
    [interventions, loadInterventions]
  );

  const updateInterventionStatut = useCallback(
    (id: string, statut: AgenceInterventionStatut) => {
      const previous = interventions.find((item) => item.id === id);
      setInterventions((prev) => prev.map((item) => (item.id === id ? { ...item, statut } : item)));
      void (async () => {
        const { error: updateError } = await supabase
          .from("maintenances")
          .update({ statut: statutToDb(statut) })
          .eq("id", id);
        if (updateError) {
          if (previous) setInterventions((prev) => prev.map((item) => (item.id === id ? previous : item)));
          dispatchDataError({
            scope: "Maintenance",
            message: "Impossible de changer le statut de l'intervention.",
            technical: describeSupabaseError(updateError),
          });
          return;
        }
        void loadInterventions();
      })();
    },
    [interventions, loadInterventions]
  );

  const addDocument = useCallback(
    (input: AgenceDocumentInput) => {
      const optimistic: AgenceDocument = { ...input, id: createUuid(), createdAt: new Date().toISOString() };
      setDocuments((prev) => [optimistic, ...prev]);
      void (async () => {
        let uploadedPath = "";
        try {
          const scope = scopeRef.current.agenceId ? scopeRef.current : await fetchScopeIds();
          const scopeId = scope.agenceId;
          if (!scopeId) throw new Error("Périmètre agence introuvable.");
          const blob = await dataUrlToBlob(input.fichierUrl);
          const path = buildDocumentPath(scopeId, input.fichierNom || input.nom);
          await uploadDocumentBlob(path, blob);
          uploadedPath = path;
          const { error: insertError } = await supabase.from("documents").insert({
            nom: input.nom,
            type_document: documentTypeToDb(input.type),
            url: path,
            date_document: input.date || null,
            immeuble_id: nullableValue(input.immeubleId),
            bien_id: nullableValue(input.bienId),
            contrat_id: nullableValue(input.contratId),
            locataire_id: nullableValue(input.locataireId),
            type_fichier: nullableValue(input.fichierType),
            taille: Math.max(0, Number(input.fichierTaille) || 0),
          });
          if (insertError) throw insertError;
          void loadDocuments();
        } catch (caught) {
          if (uploadedPath) await removeDocumentObject(uploadedPath);
          setDocuments((prev) => prev.filter((item) => item.id !== optimistic.id));
          dispatchDataError({
            scope: "Documents",
            message: "Impossible d'enregistrer le document.",
            technical: describeSupabaseError(caught),
          });
        }
      })();
      return optimistic;
    },
    [loadDocuments]
  );

  const removeDocument = useCallback(
    (id: string) => {
      const previous = documents.find((item) => item.id === id);
      const path = documentPathRef.current.get(id);
      setDocuments((prev) => prev.filter((item) => item.id !== id));
      void (async () => {
        const { error: deleteError } = await supabase.from("documents").delete().eq("id", id);
        if (deleteError) {
          if (previous) setDocuments((prev) => [previous, ...prev]);
          dispatchDataError({
            scope: "Documents",
            message: "Impossible de supprimer le document.",
            technical: describeSupabaseError(deleteError),
          });
          return;
        }
        if (path) await removeDocumentObject(path);
        void loadDocuments();
      })();
    },
    [documents, loadDocuments]
  );

  const updateDocument = useCallback(
    (id: string, input: AgenceDocumentInput) => {
      const previous = documents.find((item) => item.id === id);
      setDocuments((prev) => prev.map((item) => (item.id === id ? { ...item, ...input } : item)));
      void (async () => {
        let uploadedPath = "";
        try {
          const payload: Record<string, unknown> = {
            nom: input.nom,
            type_document: documentTypeToDb(input.type),
            date_document: input.date || null,
            immeuble_id: nullableValue(input.immeubleId),
            bien_id: nullableValue(input.bienId),
            contrat_id: nullableValue(input.contratId),
            locataire_id: nullableValue(input.locataireId),
          };
          const isNewFile = input.fichierUrl.startsWith("data:");
          if (isNewFile) {
            const scope = scopeRef.current.agenceId ? scopeRef.current : await fetchScopeIds();
            const scopeId = scope.agenceId;
            if (!scopeId) throw new Error("Périmètre agence introuvable.");
            const blob = await dataUrlToBlob(input.fichierUrl);
            const path = buildDocumentPath(scopeId, input.fichierNom || input.nom);
            await uploadDocumentBlob(path, blob);
            uploadedPath = path;
            payload.url = path;
            payload.type_fichier = nullableValue(input.fichierType);
            payload.taille = Math.max(0, Number(input.fichierTaille) || 0);
          }
          const { error: updateError } = await supabase.from("documents").update(payload).eq("id", id);
          if (updateError) throw updateError;
          if (isNewFile && uploadedPath) {
            const oldPath = documentPathRef.current.get(id);
            if (oldPath) await removeDocumentObject(oldPath);
            documentPathRef.current.set(id, uploadedPath);
          }
          void loadDocuments();
        } catch (caught) {
          if (uploadedPath) await removeDocumentObject(uploadedPath);
          if (previous) setDocuments((prev) => prev.map((item) => (item.id === id ? previous : item)));
          dispatchDataError({
            scope: "Documents",
            message: "Impossible de modifier le document.",
            technical: describeSupabaseError(caught),
          });
        }
      })();
    },
    [documents, loadDocuments]
  );

  const getImmeuble = useCallback((id: string) => immeubles.find((item) => item.id === id), [immeubles]);
  const getBien = useCallback((id: string) => biens.find((item) => item.id === id), [biens]);
  const getLocataire = useCallback((id: string) => locataires.find((item) => item.id === id), [locataires]);
  const getContrat = useCallback((id: string) => contrats.find((item) => item.id === id), [contrats]);
  const getPaiement = useCallback((id: string) => paiements.find((item) => item.id === id), [paiements]);
  const getDepense = useCallback((id: string) => depenses.find((item) => item.id === id), [depenses]);
  const getIntervention = useCallback((id: string) => interventions.find((item) => item.id === id), [interventions]);
  const getDocument = useCallback((id: string) => documents.find((item) => item.id === id), [documents]);

  const biensByImmeuble = useCallback(
    (immeubleId: string) => biens.filter((item) => item.immeubleId === immeubleId),
    [biens]
  );

  const immeublesByProprietaire = useCallback(
    (proprietaireId: string) => immeubles.filter((item) => item.proprietaireId === proprietaireId),
    [immeubles]
  );

  const biensByProprietaire = useCallback(
    (proprietaireId: string) => {
      const immeubleIds = new Set(immeubles.filter((item) => item.proprietaireId === proprietaireId).map((i) => i.id));
      return biens.filter((item) => immeubleIds.has(item.immeubleId));
    },
    [immeubles, biens]
  );

  const contratsByImmeuble = useCallback(
    (immeubleId: string) => contrats.filter((item) => item.immeubleId === immeubleId),
    [contrats]
  );

  const contratsByBien = useCallback(
    (bienId: string) => contrats.filter((item) => item.bienId === bienId),
    [contrats]
  );

  const contratsByLocataire = useCallback(
    (locataireId: string) => contrats.filter((item) => item.locataireId === locataireId),
    [contrats]
  );

  const paiementsByContrat = useCallback(
    (contratId: string) =>
      paiements
        .filter((item) => item.contratId === contratId)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [paiements]
  );

  const paiementsByContratPeriode = useCallback(
    (contratId: string, periode: string) =>
      paiements.filter((item) => item.contratId === contratId && item.periode === periode),
    [paiements]
  );

  const totalPaye = useCallback(
    (contratId: string, periode: string) =>
      paiements
        .filter((item) => item.contratId === contratId && item.periode === periode)
        .reduce((sum, item) => sum + item.montant, 0),
    [paiements]
  );

  const totalPayeContrat = useCallback(
    (contratId: string) =>
      paiements.filter((item) => item.contratId === contratId).reduce((sum, item) => sum + item.montant, 0),
    [paiements]
  );

  const depensesByImmeuble = useCallback(
    (immeubleId: string) => depenses.filter((item) => item.immeubleId === immeubleId),
    [depenses]
  );

  const depensesByBien = useCallback(
    (bienId: string) => depenses.filter((item) => item.bienId === bienId),
    [depenses]
  );

  const depensesByProprietaire = useCallback(
    (proprietaireId: string) => depenses.filter((item) => item.proprietaireId === proprietaireId),
    [depenses]
  );

  const interventionsByImmeuble = useCallback(
    (immeubleId: string) => interventions.filter((item) => item.immeubleId === immeubleId),
    [interventions]
  );

  const interventionsByBien = useCallback(
    (bienId: string) => interventions.filter((item) => item.bienId === bienId),
    [interventions]
  );

  const interventionsByProprietaire = useCallback(
    (proprietaireId: string) => interventions.filter((item) => item.proprietaireId === proprietaireId),
    [interventions]
  );

  const documentsByImmeuble = useCallback(
    (immeubleId: string) => documents.filter((item) => item.immeubleId === immeubleId),
    [documents]
  );

  const documentsByBien = useCallback(
    (bienId: string) => documents.filter((item) => item.bienId === bienId),
    [documents]
  );

  const documentsByProprietaire = useCallback(
    (proprietaireId: string) => documents.filter((item) => item.proprietaireId === proprietaireId),
    [documents]
  );

  /* -------------------------------------------------------------- */
  /* Loyers : source de vérité Supabase (v_loyers_details/v_impayes)  */
  /* -------------------------------------------------------------- */
  const loyers = useMemo<AgenceLoyer[]>(
    () => platform.loyers.filter((item) => item.scope === "agence").map(toAgenceLoyer),
    [platform.loyers]
  );

  const impayes = useMemo<AgenceLoyer[]>(
    () =>
      platform.impayes
        .filter((item) => item.scope === "agence")
        .map(toAgenceLoyer)
        .filter((loyer) => isAgenceImpaye(loyer.statut))
        .sort((a, b) => b.joursRetard - a.joursRetard),
    [platform.impayes]
  );

  const periodes = useMemo(
    () => Array.from(new Set(loyers.map((loyer) => loyer.periode))).sort((a, b) => (a < b ? 1 : -1)),
    [loyers]
  );

  const getLoyer = useCallback((id: string) => loyers.find((item) => item.id === id), [loyers]);

  const loyersByContrat = useCallback(
    (contratId: string) => loyers.filter((item) => item.contratId === contratId),
    [loyers]
  );

  const loyersByLocataire = useCallback(
    (locataireId: string) => loyers.filter((item) => item.locataireId === locataireId),
    [loyers]
  );

  const loyersByBien = useCallback((bienId: string) => loyers.filter((item) => item.bienId === bienId), [loyers]);

  const loyersByImmeuble = useCallback(
    (immeubleId: string) => loyers.filter((item) => item.immeubleId === immeubleId),
    [loyers]
  );

  const statsLoyers = useMemo<AgenceLoyersStats>(() => {
    const moisCourant = toMonthKey(new Date());
    return {
      total: loyers.length,
      payes: loyers.filter((loyer) => loyer.statut === "paye").length,
      aVenir: loyers.filter((loyer) => loyer.statut === "a-venir").length,
      enRetard: loyers.filter((loyer) => loyer.statut === "en-retard").length,
      impayes: loyers.filter((loyer) => loyer.statut === "impaye").length,
      montantDuMois: loyers
        .filter((loyer) => loyer.periode === moisCourant)
        .reduce((sum, loyer) => sum + loyer.montant, 0),
      montantImpayes: impayes.reduce((sum, loyer) => sum + loyer.reste, 0),
    };
  }, [loyers, impayes]);

  const statsPaiements = useMemo<AgencePaiementsStats>(() => {
    const moisCourant = toMonthKey(new Date());
    return {
      total: paiements.length,
      montantTotal: sumBy(paiements, (item) => item.montant),
      ceMois: paiements
        .filter((item) => toMonthKey(new Date(item.date)) === moisCourant)
        .reduce((sum, item) => sum + item.montant, 0),
    };
  }, [paiements]);

  const statsDepenses = useMemo<AgenceDepensesStats>(() => {
    const moisCourant = toMonthKey(new Date());
    return {
      total: depenses.length,
      montantTotal: sumBy(depenses, (item) => item.montant),
      ceMois: depenses
        .filter((item) => toMonthKey(new Date(item.date)) === moisCourant)
        .reduce((sum, item) => sum + item.montant, 0),
    };
  }, [depenses]);

  const statsInterventions = useMemo<AgenceInterventionsStats>(
    () => ({
      total: interventions.length,
      nouveaux: interventions.filter((item) => item.statut === "nouveau").length,
      enCours: interventions.filter((item) => item.statut === "en-cours").length,
      enAttente: interventions.filter((item) => item.statut === "en-attente").length,
      termines: interventions.filter((item) => item.statut === "termine").length,
      annules: interventions.filter((item) => item.statut === "annule").length,
      coutTotal: sumBy(interventions, (item) => item.cout),
    }),
    [interventions]
  );

  const getSummary = useCallback(
    (proprietaireId: string): ProprietaireSummary => {
      const ownerImmeubles = immeubles.filter((item) => item.proprietaireId === proprietaireId);
      const immeubleIds = new Set(ownerImmeubles.map((item) => item.id));

      const ownerBiens = biens.filter((item) => immeubleIds.has(item.immeubleId));
      const ownerContrats = contrats.filter((item) => immeubleIds.has(item.immeubleId));

      const locataireIds = new Set(ownerContrats.map((item) => item.locataireId));
      const ownerLocataires = locataires.filter((item) => locataireIds.has(item.id));

      const contratIds = new Set(ownerContrats.map((item) => item.id));
      const ownerLoyers = loyers.filter((item) => contratIds.has(item.contratId));
      const ownerPaiements = paiements.filter((item) => contratIds.has(item.contratId));
      const ownerImpayes = ownerLoyers.filter((item) => isAgenceImpaye(item.statut));

      return {
        immeubles: ownerImmeubles,
        biens: ownerBiens,
        locataires: ownerLocataires,
        contrats: ownerContrats,
        counts: {
          immeubles: ownerImmeubles.length,
          biens: ownerBiens.length,
          locataires: ownerLocataires.length,
          contrats: ownerContrats.length,
        },
        loyers: {
          count: ownerLoyers.length,
          montantTotal: sumBy(ownerLoyers, (item) => item.montant),
        },
        paiements: {
          count: ownerPaiements.length,
          montantTotal: sumBy(ownerPaiements, (item) => item.montant),
        },
        impayes: {
          count: ownerImpayes.length,
          montantTotal: sumBy(ownerImpayes, (item) => item.reste),
        },
      };
    },
    [immeubles, biens, locataires, contrats, loyers, paiements]
  );

  const value = useMemo(
    () => ({
      immeubles,
      biens,
      locataires,
      contrats,
      paiements,
      depenses,
      interventions,
      documents,
      loyers,
      impayes,
      periodes,
      statsLoyers,
      statsPaiements,
      statsDepenses,
      statsInterventions,
      addImmeuble,
      addBien,
      addLocataire,
      addContrat,
      addPaiement,
      addDepense,
      updateImmeuble,
      updateBien,
      updateLocataire,
      updateContrat,
      updateDepense,
      removeImmeuble,
      removeBien,
      removeLocataire,
      removeContrat,
      removePaiement,
      removeDepense,
      updateIntervention,
      addIntervention,
      removeIntervention,
      updateInterventionStatut,
      updateDocument,
      addDocument,
      removeDocument,
      getImmeuble,
      getBien,
      getLocataire,
      getContrat,
      getPaiement,
      getDepense,
      getIntervention,
      getDocument,
      getLoyer,
      biensByImmeuble,
      biensByProprietaire,
      immeublesByProprietaire,
      contratsByImmeuble,
      contratsByBien,
      contratsByLocataire,
      loyersByContrat,
      loyersByLocataire,
      loyersByBien,
      loyersByImmeuble,
      paiementsByContrat,
      paiementsByContratPeriode,
      totalPaye,
      totalPayeContrat,
      depensesByImmeuble,
      depensesByBien,
      depensesByProprietaire,
      interventionsByImmeuble,
      interventionsByBien,
      interventionsByProprietaire,
      documentsByImmeuble,
      documentsByBien,
      documentsByProprietaire,
      getSummary,
    }),
    [
      immeubles,
      biens,
      locataires,
      contrats,
      paiements,
      depenses,
      interventions,
      documents,
      loyers,
      impayes,
      periodes,
      statsLoyers,
      statsPaiements,
      statsDepenses,
      statsInterventions,
      addImmeuble,
      addBien,
      addLocataire,
      addContrat,
      addPaiement,
      addDepense,
      updateImmeuble,
      updateBien,
      updateLocataire,
      updateContrat,
      updateDepense,
      removeImmeuble,
      removeBien,
      removeLocataire,
      removeContrat,
      removePaiement,
      removeDepense,
      updateIntervention,
      addIntervention,
      removeIntervention,
      updateInterventionStatut,
      updateDocument,
      addDocument,
      removeDocument,
      getImmeuble,
      getBien,
      getLocataire,
      getContrat,
      getPaiement,
      getDepense,
      getIntervention,
      getDocument,
      getLoyer,
      biensByImmeuble,
      biensByProprietaire,
      immeublesByProprietaire,
      contratsByImmeuble,
      contratsByBien,
      contratsByLocataire,
      loyersByContrat,
      loyersByLocataire,
      loyersByBien,
      loyersByImmeuble,
      paiementsByContrat,
      paiementsByContratPeriode,
      totalPaye,
      totalPayeContrat,
      depensesByImmeuble,
      depensesByBien,
      depensesByProprietaire,
      interventionsByImmeuble,
      interventionsByBien,
      interventionsByProprietaire,
      documentsByImmeuble,
      documentsByBien,
      documentsByProprietaire,
      getSummary,
    ]
  );

  return <AgenceDataContext.Provider value={value}>{children}</AgenceDataContext.Provider>;
}

export function useAgenceData(): AgenceDataContextValue {
  const context = useContext(AgenceDataContext);
  if (!context) {
    throw new Error("useAgenceData doit être utilisé à l'intérieur de AgenceDataProvider");
  }
  return context;
}